import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { BRAND_COLORS, SURFACE_COLORS } from '../brand/tokens';

/** Static base and animated target of the page backdrop, from the brand tokens. */
const BASE_GRADIENT = `linear-gradient(135deg, ${SURFACE_COLORS.surface} 0%, ${SURFACE_COLORS.sheet} 50%, ${SURFACE_COLORS.overlay} 100%)`;
const SHIFT_GRADIENT = `linear-gradient(135deg, ${SURFACE_COLORS.surface} 0%, ${SURFACE_COLORS.sheet} 25%, ${SURFACE_COLORS.overlay} 50%, ${SURFACE_COLORS.elevated} 75%, ${BRAND_COLORS['lipstick-dark']} 100%)`;
const EDGE_GRADIENT = `radial-gradient(ellipse at center, transparent 0%, ${BRAND_COLORS.lipstick}1A 50%, ${SURFACE_COLORS.surface}CC 100%)`;

interface DarkVeilBackgroundProps {
  hueShift?: boolean;
  intensity?: number;
  className?: string;
}

const DarkVeilBackground: React.FC<DarkVeilBackgroundProps> = ({
  hueShift = true,
  intensity = 0.8,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const veilRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    if (!containerRef.current || !veilRef.current || !particlesRef.current)
      return;

    // Kill any existing animation
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    // Create the main timeline
    timelineRef.current = gsap.timeline({ repeat: -1 });

    // Dark veil pulsing animation
    timelineRef.current.to(veilRef.current, {
      opacity: intensity,
      duration: 3,
      ease: 'power2.inOut',
      yoyo: true,
      repeat: -1,
    });

    // Red hue shift animation
    if (hueShift) {
      timelineRef.current.to(
        veilRef.current,
        {
          filter: 'hue-rotate(15deg) saturate(1.2) brightness(0.9)',
          duration: 4,
          ease: 'power2.inOut',
          yoyo: true,
          repeat: -1,
        },
        0
      );
    }

    // Floating particles animation
    const particles = particlesRef.current.children;
    Array.from(particles).forEach((particle, index) => {
      gsap.to(particle, {
        y: -20,
        x: Math.sin(index) * 10,
        duration: 3 + Math.random() * 2,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: -1,
        delay: index * 0.2,
      });

      gsap.to(particle, {
        opacity: 0.3 + Math.random() * 0.4,
        duration: 2 + Math.random() * 3,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: -1,
        delay: index * 0.1,
      });
    });

    // Background gradient shift
    timelineRef.current.to(
      containerRef.current,
      {
        background: SHIFT_GRADIENT,
        duration: 8,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: -1,
      },
      0
    );

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, [hueShift, intensity]);

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    for (let i = 0; i < 20; i++) {
      particles.push(
        <div
          key={i}
          className="absolute w-1 h-1 bg-hc-accent rounded-full opacity-30"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 pointer-events-none overflow-hidden ${className}`}
      style={{
        background: BASE_GRADIENT,
        zIndex: -1,
      }}
    >
      {/* Dark Veil Layer */}
      <div
        ref={veilRef}
        className="absolute inset-0 bg-gradient-to-br from-black/40 via-hc-accent-strong/20 to-black/60"
        style={{
          mixBlendMode: 'multiply',
          filter: 'hue-rotate(0deg)',
        }}
      />

      {/* Floating Particles */}
      <div ref={particlesRef} className="absolute inset-0">
        {generateParticles()}
      </div>

      {/* Animated Mesh Overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="mesh"
              x="0"
              y="0"
              width="100"
              height="100"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="50"
                cy="50"
                r="1"
                fill={BRAND_COLORS.lipstick}
                opacity="0.3"
              >
                <animate
                  attributeName="r"
                  values="1;3;1"
                  dur="4s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.3;0.8;0.3"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle
                cx="25"
                cy="25"
                r="0.5"
                fill={BRAND_COLORS.blush}
                opacity="0.2"
              >
                <animate
                  attributeName="r"
                  values="0.5;2;0.5"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle
                cx="75"
                cy="75"
                r="0.8"
                fill={BRAND_COLORS['lipstick-deep']}
                opacity="0.25"
              >
                <animate
                  attributeName="r"
                  values="0.8;2.5;0.8"
                  dur="5s"
                  repeatCount="indefinite"
                />
              </circle>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#mesh)" />
        </svg>
      </div>

      {/* Radial Gradient Overlay */}
      <div
        className="absolute inset-0 opacity-60"
        style={{ background: EDGE_GRADIENT }}
      />
    </div>
  );
};

export default DarkVeilBackground;
